<template>
  <div
    v-if="isOpen && hasSteps"
    class="tour-home"
    aria-live="polite"
    @wheel.prevent
    @touchmove.prevent
  >
    <div class="tour-home__overlay" @click="close"></div>

    <div class="tour-home__card" :style="cardInlineStyle">
      <div class="tour-home__card-header">
        <h3 class="tour-home__title">{{ currentStep.title || 'Tour' }}</h3>

        <button
          type="button"
          class="tour-home__close"
          aria-label="Cerrar"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </button>
      </div>

      <div class="tour-home__content">
        {{ currentDescription }}
      </div>

      <div class="tour-home__footer">
        <div class="tour-home__actions">
          <v-btn
            v-if="canGoPrevious"
            small
            class="tour-home__previous"
            @click="handlePreviousAction"
          >
            {{ currentStep.previousLabel || 'Anterior' }}
          </v-btn>

          <v-btn
            v-if="canGoNext"
            small
            color="primary"
            class="tour-home__next"
            @click="handleNextAction"
          >
            {{ currentStep.nextLabel || 'Siguiente' }}
          </v-btn>

          <v-btn
            v-if="isLastTourStep && isLastDescription"
            small
            color="primary"
            class="tour-home__finish"
            @click="finishTour('final-button')"
          >
            {{ currentStep.finishLabel || 'Finalizar' }}
          </v-btn>
        </div>

        <span class="tour-home__counter">
          {{ currentDescriptionIndex + 1 }} de {{ totalDescriptions }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TourHomeMobile',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    steps: {
      type: Array,
      default: () => ([]),
    },
    cardWidth: {
      type: Number,
      default: 420,
    },
    offsetY: {
      type: Number,
      default: 12,
    },
    stepIndex: {
      type: Number,
      default: 0,
    },
    totalTourSteps: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      isOpen: false,
      currentStepIndex: 0,
      currentDescriptionIndex: 0,
      activeTarget: null,
      cardStyle: {
        top: '24px',
        left: '24px',
      },
      previousBodyOverflow: null,
      previousHtmlOverflow: null,
      previousBodyPosition: null,
      previousBodyTop: null,
      previousBodyWidth: null,
    };
  },
  computed: {
    hasSteps() {
      return Array.isArray(this.steps) && this.steps.length > 0;
    },
    totalSteps() {
      return this.steps.length;
    },
    currentStep() {
      return this.steps[this.currentStepIndex] || {};
    },
    currentStepDescriptions() {
      const hasDescriptions = Array.isArray(this.currentStep.descriptions)
        && this.currentStep.descriptions.length > 0;

      if (hasDescriptions) {
        return this.currentStep.descriptions;
      }

      if (typeof this.currentStep.description === 'string' && this.currentStep.description.trim()) {
        return [this.currentStep.description];
      }

      return [''];
    },
    totalDescriptions() {
      return this.currentStepDescriptions.length;
    },
    currentDescription() {
      return this.currentStepDescriptions[this.currentDescriptionIndex] || '';
    },
    isLastDescription() {
      return this.currentDescriptionIndex + 1 >= this.totalDescriptions;
    },
    isLastTourStep() {
      return this.stepIndex + 1 >= this.totalTourSteps;
    },
    isLastStep() {
      return this.currentStepIndex + 1 >= this.totalSteps;
    },
    canGoPrevious() {
      return !(this.stepIndex === 0 && this.currentDescriptionIndex === 0);
    },
    canGoNext() {
      return !(this.isLastTourStep && this.isLastDescription);
    },
    cardMode() {
      if (this.currentStep.menu === true || this.currentStep.app === 'menu') {
        return 'menu';
      }

      if (this.currentStep.perfil === true || this.currentStep.app === 'perfil') {
        return 'perfil';
      }

      return 'home';
    },
    cardInlineStyle() {
      if (this.cardMode === 'menu') {
        const computedTop = parseFloat(this.cardStyle.top);
        const menuTop = Number.isFinite(computedTop)
          ? `${computedTop - 10}px`
          : this.cardStyle.top;

        return {
          ...this.cardStyle,
          left: '10px',
          top: menuTop,
        };
      }

      if (this.cardMode === 'perfil') {
        return {
          ...this.cardStyle,
          top: '72px',
          left: 'none',
          right: '10px',
        };
      }

      return this.cardStyle;
    },
    currentPlacement() {
      const placement = typeof this.currentStep.placement === 'string'
        ? this.currentStep.placement.toLowerCase()
        : 'auto';

      return ['auto', 'top', 'bottom'].includes(placement) ? placement : 'auto';
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (val) {
          this.open();
          return;
        }
        this.close(false);
      },
    },
    currentStepIndex() {
      this.$nextTick(() => {
        this.activateCurrentStep();
      });
    },
  },
  methods: {
    getDescriptionsForStep(step) {
      const hasDescriptions = Array.isArray(step && step.descriptions)
        && step.descriptions.length > 0;

      if (hasDescriptions) {
        return step.descriptions;
      }

      if (typeof step.description === 'string' && step.description.trim()) {
        return [step.description];
      }

      return [''];
    },
    open() {
      if (!this.hasSteps) {
        return;
      }

      this.isOpen = true;
      this.currentStepIndex = 0;
      this.currentDescriptionIndex = 0;
      this.lockScroll();
      this.$nextTick(() => {
        this.activateCurrentStep();
      });

      window.addEventListener('resize', this.activateCurrentStep, {
        passive: true,
      });
      window.addEventListener('scroll', this.activateCurrentStep, {
        passive: true,
      });
      window.addEventListener('keydown', this.handleKeydown);
    },
    close(emitModel = true) {
      this.isOpen = false;
      this.removeTargetHighlight();
      this.unlockScroll();

      window.removeEventListener('resize', this.activateCurrentStep);
      window.removeEventListener('scroll', this.activateCurrentStep);
      window.removeEventListener('keydown', this.handleKeydown);

      if (emitModel) {
        this.$emit('input', false);
      }
      this.$emit('close');
    },
    next() {
      if (!this.isLastDescription) {
        this.currentDescriptionIndex += 1;
        return;
      }

      if (!this.isLastStep) {
        this.currentStepIndex += 1;
        this.currentDescriptionIndex = 0;
        this.$emit('step-change', this.currentStepIndex);
      }
    },
    handleNextAction() {
      if (this.isLastDescription && !this.isLastTourStep) {
        this.finishTour('next-action');
        return;
      }

      this.next();
    },
    handlePreviousAction() {
      if (this.currentDescriptionIndex > 0) {
        this.previous();
        return;
      }

      if (this.stepIndex > 0) {
        this.goToPreviousTourStep();
      }
    },
    previous() {
      if (this.currentDescriptionIndex > 0) {
        this.currentDescriptionIndex -= 1;
        return;
      }

      if (this.currentStepIndex > 0) {
        const previousStepIndex = this.currentStepIndex - 1;
        const previousDescriptions = this.getDescriptionsForStep(
          this.steps[previousStepIndex] || {},
        );

        this.currentStepIndex = previousStepIndex;
        this.currentDescriptionIndex = Math.max(previousDescriptions.length - 1, 0);
        this.$emit('step-change', this.currentStepIndex);
      }
    },
    goToPreviousTourStep() {
      const previousIndex = Math.max(this.stepIndex - 1, 0);
      localStorage.setItem('tourStepsIndex', previousIndex.toString());
      this.$emit('finished');
      this.close();
    },
    finishTour(source = 'unknown') {
      const index = parseInt(localStorage.getItem('tourStepsIndex') || '0', 10);
      const isTourFullyCompleted = source === 'final-button'
        && this.isLastTourStep
        && this.isLastDescription;

      if (isTourFullyCompleted) {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }

      localStorage.setItem('tourStepsIndex', (index + 1).toString());
      this.$emit('finished');
      this.close();
    },
    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
      }
    },
    lockScroll() {
      this.previousHtmlOverflow = document.documentElement.style.overflow;
      this.previousBodyOverflow = document.body.style.overflow;
      this.previousBodyPosition = document.body.style.position;
      this.previousBodyTop = document.body.style.top;
      this.previousBodyWidth = document.body.style.width;
    },
    unlockScroll() {
      document.documentElement.style.overflow = this.previousHtmlOverflow || '';
      document.body.style.overflow = this.previousBodyOverflow || '';
      document.body.style.position = this.previousBodyPosition || '';
      document.body.style.top = this.previousBodyTop || '';
      document.body.style.width = this.previousBodyWidth || '';
    },
    findTargetElement(selector) {
      const normalizedSelector = this.normalizeSelector(selector);
      if (!normalizedSelector) {
        return null;
      }

      if (normalizedSelector.startsWith('#') && !normalizedSelector.includes(' ')) {
        return document.getElementById(normalizedSelector.slice(1));
      }

      if (!normalizedSelector.includes(' ')) {
        return document.getElementById(normalizedSelector);
      }

      return document.querySelector(normalizedSelector);
    },
    normalizeSelector(selector) {
      if (typeof selector !== 'string') {
        return '';
      }

      let normalized = selector.trim().replace(/^(?:["']+)|(?:["']+)$/g, '');
      if (!normalized) {
        return '';
      }

      if (!normalized.startsWith('#') && !normalized.includes(' ')) {
        normalized = `#${normalized}`;
      }

      return normalized;
    },
    getScrollHost() {
      return document.querySelector('.v-main__wrap')
        || document.querySelector('.v-main')
        || document.scrollingElement
        || document.documentElement;
    },
    getScrollOffsets() {
      const scrollHost = this.getScrollHost();
      const usesDocumentScroll = scrollHost === document.body
        || scrollHost === document.documentElement
        || scrollHost === document.scrollingElement;

      if (usesDocumentScroll) {
        return {
          top: window.pageYOffset || window.scrollY || document.documentElement.scrollTop || 0,
          left: window.pageXOffset || window.scrollX || document.documentElement.scrollLeft || 0,
        };
      }

      return {
        top: scrollHost.scrollTop || 0,
        left: scrollHost.scrollLeft || 0,
      };
    },
    getPageOffsets() {
      const { top, left } = this.getScrollOffsets();

      return { top, left };
    },
    getTargetMetrics(selector) {
      const target = this.findTargetElement(selector);

      if (!target) {
        return null;
      }

      const rect = target.getBoundingClientRect();
      const pageOffsets = this.getPageOffsets();

      return {
        target,
        absoluteTop: rect.top + pageOffsets.top,
        absoluteLeft: rect.left + pageOffsets.left,
        height: rect.height,
        width: rect.width,
        viewportTop: rect.top,
        viewportLeft: rect.left,
      };
    },
    getHighlightTarget(target) {
      if (this.cardMode !== 'menu' || !target) {
        return target;
      }

      return target.querySelector('.v-icon, i, svg') || target;
    },
    getCardHeight() {
      const card = this.$el ? this.$el.querySelector('.tour-home__card') : null;
      return card ? card.offsetHeight : 190;
    },
    getCardTop(targetMetrics, cardHeight, viewportHeight) {
      const targetBottom = targetMetrics.viewportTop + targetMetrics.height;
      const topPosition = Math.max(12, targetMetrics.viewportTop - this.offsetY - cardHeight);
      const bottomPosition = targetBottom + this.offsetY;
      const canRenderBottom = bottomPosition + cardHeight < viewportHeight;

      if (this.currentPlacement === 'bottom') {
        return canRenderBottom ? bottomPosition : topPosition;
      }

      if (this.currentPlacement === 'top') {
        return topPosition;
      }

      return canRenderBottom ? bottomPosition : topPosition;
    },
    activateCurrentStep() {
      if (!this.isOpen) {
        return;
      }

      this.removeTargetHighlight();

      const { selector } = this.currentStep;
      if (typeof selector !== 'string' || !selector.trim()) {
        this.cardStyle = {
          top: '24px',
          left: '24px',
        };
        return;
      }

      const targetMetrics = this.getTargetMetrics(selector);
      if (!targetMetrics) {
        this.cardStyle = {
          top: '24px',
          left: '24px',
        };
        return;
      }

      this.activeTarget = this.getHighlightTarget(targetMetrics.target);
      this.activeTarget.classList.add('tour-home__target--active');

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const cardHeight = this.getCardHeight();

      const positionedMetrics = this.getTargetMetrics(selector) || targetMetrics;
      const width = Math.min(this.cardWidth, viewportWidth - 24);
      const maxLeft = Math.max(12, viewportWidth - width - 12);
      const left = Math.min(Math.max(positionedMetrics.viewportLeft, 12), maxLeft);
      const top = this.getCardTop(positionedMetrics, cardHeight, viewportHeight);

      this.cardStyle = {
        width: `${width}px`,
        left: `${left}px`,
        top: `${top}px`,
      };
    },
    removeTargetHighlight() {
      if (this.activeTarget) {
        this.activeTarget.classList.remove('tour-home__target--active');
      }
      this.activeTarget = null;
    },
  },
  beforeDestroy() {
    this.close(false);
  },
};
</script>

<style lang="scss" scoped>
.tour-home {
  position: fixed;
  inset: 0;
  z-index: 1800;
  background-color: #524f4f59;
  font-family: 'Source Sans Pro';
}

.tour-home__overlay {
  position: absolute;
  inset: 0;
  background: rgb(55 52 52 / 55%);
}

.tour-home__card {
  position: fixed;
  z-index: 1802;
  background: #fff;
  color: black;
  border-radius: 4px;
  padding: 1rem;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.35);
}

.tour-home__card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.tour-home__title {
  margin: 0;
  font-size: 16px;
  line-height: 1.1;
  font-weight: 500;
}

.tour-home__close {
  appearance: none;
  border: 0;
  background: transparent;
  color: #27846d;
  font-size: 1.8rem;
  line-height: 1;
  cursor: pointer;
}

.tour-home__content {
  margin-top: 1rem;
  font-size: 15px;
  line-height: 1.35;
}

.tour-home__footer {
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.tour-home__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tour-home__previous {
  color: #fff !important;
  background-color:  rgb(128, 128, 128) !important;
  text-transform: none;
}

.tour-home__next {
  text-transform: none;
}

.tour-home__finish {
  text-transform: none;
}

.tour-home__counter {
  font-size: 15px;
  color: #27846d;
}

@media (max-width: 768px) {
  .tour-home__title {
    font-size: 16px;
  }

  .tour-home__content {
    font-size: 15px;
  }

  .tour-home__counter {
    font-size: 15px;
  }
}
</style>

<style lang="scss">
.tour-home__target--active {
  position: relative !important;
  z-index: 1801 !important;
  box-shadow:
    0 0 0 3px rgba(45, 167, 110, 0.85),   /* #2da76e */
    0 0 0 8px rgba(98, 188, 76, 0.45) !important; /* #62bc4c */
  border-radius: 4px !important;
  pointer-events: none !important;
}
</style>
